import {useEffect, useState} from 'react';
import {utmSourceLogo} from './constants';

interface PartnerBrandingEntry {
    utm_source: string;
    display_name: string;
    logo_url: string;
}

export interface PartnerLogoMap {
    [utmSource: string]: string;
}

let cachedMergedMap: PartnerLogoMap | null = null;
let cachedStrapiKeys: Set<string> | null = null;
let cacheTimestamp = 0;
const CACHE_TTL = 10 * 60 * 1000; // 10 minutes

function buildMergedMap(strapiEntries: PartnerBrandingEntry[]): PartnerLogoMap {
    // Start with hardcoded mappings (all keys already lowercase)
    const merged: PartnerLogoMap = {};
    for (const [key, value] of Object.entries(utmSourceLogo)) {
        merged[key.toLowerCase()] = value;
    }

    // Strapi entries override hardcoded ones (case-insensitive)
    for (const entry of strapiEntries) {
        merged[entry.utm_source.toLowerCase()] = entry.logo_url;
    }

    return merged;
}

export function usePartnerBrandings(): {logoMap: PartnerLogoMap; strapiKeys: Set<string>; isLoaded: boolean} {
    const [logoMap, setLogoMap] = useState<PartnerLogoMap>(() => {
        if (cachedMergedMap) return cachedMergedMap;
        // Initial state: just the hardcoded mappings
        const initial: PartnerLogoMap = {};
        for (const [key, value] of Object.entries(utmSourceLogo)) {
            initial[key.toLowerCase()] = value;
        }
        return initial;
    });
    const [strapiKeys, setStrapiKeys] = useState<Set<string>>(() => cachedStrapiKeys ?? new Set());
    const [isLoaded, setIsLoaded] = useState(!!cachedMergedMap);

    useEffect(() => {
        // Skip fetch if there's no utm_source in the URL — the Strapi data won't be used
        const utmSource =
            typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('utm_source') : null;
        if (!utmSource) return;

        const now = Date.now();
        if (cachedMergedMap && cachedStrapiKeys && now - cacheTimestamp < CACHE_TTL) {
            setLogoMap(cachedMergedMap);
            setStrapiKeys(cachedStrapiKeys);
            setIsLoaded(true);
            return;
        }

        fetch('/api/partner-brandings')
            .then((res) => res.json())
            .then((entries: PartnerBrandingEntry[]) => {
                const merged = buildMergedMap(entries);
                const keys = new Set(entries.map((e) => e.utm_source.toLowerCase()));
                cachedMergedMap = merged;
                cachedStrapiKeys = keys;
                cacheTimestamp = Date.now();
                setLogoMap(merged);
                setStrapiKeys(keys);
                setIsLoaded(true);
            })
            .catch((err) => {
                console.error('Failed to fetch partner brandings:', err);
                setIsLoaded(true);
            });
    }, []);

    return {logoMap, strapiKeys, isLoaded};
}
